import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import back from 'asset/img/back.png';
import gold from 'asset/img/gold.svg';
import background from 'asset/img/bk-person.webp';
import white from 'asset/img/white.webp';
import { getCheckinStatus, checkinSign, checkinMakeup, getCheckinHonors, getGardenInfo } from 'network';
import { playCheckinSignSound, playCheckinMakeupSound } from 'utils/soundEffect';
import CheckinCalendar from './CheckinCalendar';
import CheckinSignBtn from './CheckinSignBtn';
import CheckinMakeupList from './CheckinMakeupList';
import CheckinHonorsPreview from './CheckinHonorsPreview';
import DakaRewardModal from './DakaRewardModal';
import Loadding from 'components/Loadding';
import './index.scss';

const Checkin = () => {
    const navigate = useNavigate();
    const isMounted = useRef(true);
    const [status, setStatus] = useState(null);
    const [honors, setHonors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [signLoading, setSignLoading] = useState(false);
    const [coinRemain, setCoinRemain] = useState(0);
    const [dakaVisible, setDakaVisible] = useState(false);
    const [dakaDay, setDakaDay] = useState(0);

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    const fetchStatus = useCallback(() => {
        getCheckinStatus({}).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0 && res?.data) setStatus(res.data);
            else message.error(res?.msg || '获取签到状态失败');
        }).catch(() => message.error('获取签到状态失败'));
    }, []);

    const fetchHonors = useCallback(() => {
        getCheckinHonors({}).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0 && res?.data) setHonors(res.data);
        }).catch(() => {});
    }, []);

    const fetchCoin = useCallback(() => {
        getGardenInfo({}).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0 && res?.data?.garden_info) {
                setCoinRemain(res.data.garden_info.coin_remain ?? 0);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        isMounted.current = true;
        setLoading(true);
        Promise.all([
            getCheckinStatus({}),
            getCheckinHonors({}),
            getGardenInfo({}),
        ]).then(([statusRes, honorsRes, gardenRes]) => {
            if (!isMounted.current) return;
            if (statusRes?.code === 0 && statusRes?.data) setStatus(statusRes.data);
            if (honorsRes?.code === 0 && honorsRes?.data) setHonors(honorsRes.data);
            if (gardenRes?.code === 0) {
                const coin = gardenRes?.data?.garden_info?.coin_remain ?? 0;
                setCoinRemain(coin);
            }
        }).catch(() => message.error('加载失败')).finally(() => {
            if (isMounted.current) setLoading(false);
        });
        return () => { isMounted.current = false; };
    }, []);

    const showDakaIfNeeded = useCallback((prevRealDays) => {
        const newReal = (prevRealDays ?? 0) + 1;
        if (newReal >= 1 && newReal <= 7) {
            setDakaDay(newReal);
            setDakaVisible(true);
        }
    }, []);

    const handleSign = useCallback(() => {
        if (signLoading || !status?.can_sign) return;
        setSignLoading(true);
        const prevReal = status?.total_real_days ?? 0;
        checkinSign({}).then((res) => {
            if (!isMounted.current) return;
            if (res?.code !== 0) {
                message.error(res?.msg || '签到失败');
                return;
            }
            const coin = res?.data?.coin ?? 0;
            playCheckinSignSound(); // 打卡随机 1/2/3
            message.success(`签到成功，获得 ${coin} 金币`);
            showDakaIfNeeded(prevReal);
            fetchStatus();
            fetchHonors();
            setTimeout(fetchCoin, 400);
        }).catch(() => {
            if (isMounted.current) message.error('签到失败');
        }).finally(() => {
            if (isMounted.current) setSignLoading(false);
        });
    }, [status?.can_sign, status?.total_real_days, signLoading, fetchStatus, fetchHonors, fetchCoin, showDakaIfNeeded]);

    const handleMakeup = useCallback((date) => {
        if (signLoading) return;
        setSignLoading(true);
        checkinMakeup({ date }).then((res) => {
            if (!isMounted.current) return;
            if (res?.code !== 0) {
                message.error(res?.msg || '补签失败');
                return;
            }
            const coin = res?.data?.coin ?? 0;
            playCheckinMakeupSound(); // 补签 0
            message.success(`补签成功，获得 ${coin} 金币`);
            fetchStatus();
            fetchHonors();
            setTimeout(fetchCoin, 400);
        }).catch(() => {
            if (isMounted.current) message.error('补签失败');
        }).finally(() => {
            if (isMounted.current) setSignLoading(false);
        });
    }, [signLoading, fetchStatus, fetchHonors, fetchCoin]);

    const handleBack = () => navigate(-1);
    const handleHonorsClick = () => navigate('/honors');

    if (loading && !status) {
        return (
            <div className="checkin-page">
                <img src={background} className="checkin-bg" alt="" />
                <img src={white} className="checkin-bg checkin-bg-white" alt="" />
                <Loadding visible />
                <div className="checkin-back-wrap" onClick={handleBack}>
                    <img src={back} alt="返回" />
                </div>
            </div>
        );
    }

    return (
        <div className="checkin-page">
            <img src={background} className="checkin-bg" alt="" />
            <img src={white} className="checkin-bg checkin-bg-white" alt="" />
            <div className="checkin-back-wrap" onClick={handleBack}>
                <img src={back} alt="返回" />
            </div>
            <div className="checkin-header">
                <h1>每日签到</h1>
                <div className="checkin-coin">
                    <img src={gold} alt="金币" />
                    <span>{coinRemain}</span>
                </div>
            </div>
            <div className="checkin-stats">
                <div className="stat">
                    <span className="value">{status?.streak ?? 0}</span>
                    <span className="label">连续签到</span>
                </div>
                <div className="stat">
                    <span className="value">{status?.total_days ?? 0}</span>
                    <span className="label">累计签到</span>
                </div>
                {status?.today_signed && (
                    <div className="stat stat-signed">
                        <span className="value">✓</span>
                        <span className="label">今日已签到</span>
                    </div>
                )}
            </div>
            <CheckinCalendar
                monthSigned={status?.month_signed ?? []}
                year={year}
                month={month}
            />
            {!status?.today_signed && (
                <div className="checkin-sign-area">
                    <CheckinSignBtn
                        canSign={status?.can_sign}
                        todayDoneCount={status?.today_done_count ?? 0}
                        loading={signLoading}
                        onSign={handleSign}
                    />
                </div>
            )}
            <CheckinMakeupList
                canMakeupDates={status?.can_makeup_dates ?? []}
                makeupLeft={status?.makeup_left ?? 0}
                makeupCoinCost={status?.makeup_coin_cost ?? 20}
                coinRemain={coinRemain}
                loading={signLoading}
                onMakeup={handleMakeup}
            />
            <CheckinHonorsPreview
                title={honors?.title}
                badges={honors?.badges ?? []}
                onClick={handleHonorsClick}
            />
            <DakaRewardModal
                visible={dakaVisible}
                day={dakaDay}
                onClose={() => setDakaVisible(false)}
            />
        </div>
    );
};

export default Checkin;
